import { logger } from '@automata/utils';
import type {
  ActionTaskInput,
  CodeTaskInput,
  ConditionalInput,
  ContinuousLoadingInput,
  DataConnectorInput,
  WorkflowInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import Graph from 'graphology';

import { getActionTask } from './getActionTask';
import { getActionTaskLabwareSources } from './getActionTaskLabwareSources';
import { getCodeTask } from './getCodeTask';
import { getConditionalTask } from './getConditionalTask';
import { getContinuousLoadingTask } from './getContinuousLoadingTask';
import { getExportDataTask } from './getExportDataTask';
import {
  type AllTaskProperties,
  type CodeTaskProperties,
  type ConditionalTaskProperties,
  type DataExportTaskProperties,
  type GraphEdges,
  type InstrumentForLocation,
  PropertiesType,
  type TaskProperties,
} from '../../../types';
import { dealiasActionTaskInput } from '../../../utils/workcellActionAliases';
import { LabwareAndSourceTaskByTaskId } from '../../selectors/selectLabwareAndSourceNodeByTaskId';
import type { LabwareByHandleIndexByTaskId } from '../../selectors/types';
import type {
  DataDependenciesByTaskId,
  MaybeWorkflowSerializationErrors,
  TimeEstimateValuesByTaskId,
  WorkflowSerializationError,
} from '../../shared/types';
import type { DriverActionConfigsByActionNameByVersionedDriverName } from '../../workflow/types';
import { TaskDependenciesByTaskId } from '../getJsonTaskDependencies';
import { err, ok } from '../utils';

// TODO(IL, 2025-01-16) should be named something like "getTasks" / "getAllTasks" --
// or preferable IMO just merged into tasksToJson. (It's essentially the body of tasksToJson,
// but is factored out in order for some "forSave" pieces to work)
export const walkTaskListForValidation = (
  taskIds: string[],
  _: Graph,
  labwareIdsForTaskByTaskId: LabwareByHandleIndexByTaskId,
  taskPropertiesById: Record<string, AllTaskProperties>,
  timeEstimateValuesByTaskId: TimeEstimateValuesByTaskId,
  taskDependenciesByTaskId: TaskDependenciesByTaskId,
  dataDependenciesByTaskId: DataDependenciesByTaskId,
  instrumentsById: Record<string, InstrumentForLocation>,
  driverActionConfigs: DriverActionConfigsByActionNameByVersionedDriverName,
  labwareAndSourceTaskByTaskId: LabwareAndSourceTaskByTaskId,
  labwareDependencyEdges: GraphEdges
): MaybeWorkflowSerializationErrors<WorkflowInput['tasks']> => {
  const validTasks: WorkflowInput['tasks'] = {};
  const errors: WorkflowSerializationError[] = [];
  const childTaskIds = new Set<string>();

  const hoistedTaskIds = new Set<string>();
  for (const taskId of taskIds) {
    const taskProps = taskPropertiesById[taskId];
    if (!taskProps || taskProps.propertiesType !== 'action') continue;

    const instrument =
      taskProps.instrumentId != null
        ? instrumentsById[taskProps.instrumentId]
        : null;
    if (!instrument) continue;

    const labwareIdsInTask = Object.values(
      labwareIdsForTaskByTaskId[taskId] ?? []
    ).flatMap((labwareId) => (labwareId === null ? [] : [labwareId]));

    const userSpecifiedSlot =
      typeof taskProps.taskParameters?.slot === 'string'
        ? taskProps.taskParameters.slot
        : null;

    const labwareSources = getActionTaskLabwareSources({
      instrument,
      labwareIdsInTask,
      taskProperties: taskProps,
      userSpecifiedSlot,
      labwareIdsForTaskByTaskId,
      labwareAndSourceTaskByTaskId,
      allTaskPropertiesById: taskPropertiesById,
    });

    if (labwareSources.isError) {
      errors.push(...labwareSources.errors);
      continue;
    }

    const hasMultiSource = Object.values(labwareSources.data).some(
      (ls) =>
        typeof ls.source_task === 'object' &&
        ls.source_task !== null &&
        Array.isArray(ls.source_task.task_ids) &&
        ls.source_task.task_ids.length > 1
    );

    if (hasMultiSource) {
      hoistedTaskIds.add(taskId);
    }
  }

  const buildTaskById = (
    taskId: string
  ):
    | ActionTaskInput
    | CodeTaskInput
    | DataConnectorInput
    | ConditionalInput
    | ContinuousLoadingInput
    | null => {
    const taskProperties = taskPropertiesById[taskId];
    if (!taskProperties) return null;

    switch (taskProperties.propertiesType) {
      case 'code':
        return buildCodeTask(taskId, taskProperties);

      case 'dataExport':
        return buildExportTask(taskId, taskProperties);

      case 'action':
        return buildActionTask(taskId, taskProperties);

      case 'condition':
        return buildConditionTask(taskId, taskProperties);

      case 'continuousLoading':
        return buildContinuousLoadingTask(taskId);

      default:
        return null;
    }
  };

  const buildCodeTask = (taskId: string, taskProperties: CodeTaskProperties) =>
    getCodeTask({
      taskId,
      taskProperties,
      taskDependencies: taskDependenciesByTaskId[taskId] ?? [],
      dataDependenciesByTaskId,
    });

  const buildExportTask = (
    taskId: string,
    taskProperties: DataExportTaskProperties
  ) =>
    getExportDataTask({
      taskId,
      taskProperties,
      taskDependencies: taskDependenciesByTaskId[taskId] ?? [],
      dataDependenciesByTaskId,
    });

  const buildActionTask = (taskId: string, taskProperties: TaskProperties) => {
    const instrument =
      taskProperties.instrumentId != null
        ? instrumentsById[taskProperties.instrumentId]
        : null;
    if (!instrument) return null;

    const actionTask = getActionTask({
      taskId,
      taskProperties,
      taskDependencies: taskDependenciesByTaskId[taskId] ?? [],
      labwareIdsForTaskByTaskId,
      timeEstimateValuesByTaskId,
      instrument,
      labwareAndSourceTaskByTaskId,
      allTaskPropertiesById: taskPropertiesById,
    });

    if (actionTask.isError) {
      errors.push(...actionTask.errors);
      return null;
    }

    return dealiasActionTaskInput(
      actionTask.data,
      driverActionConfigs,
      instrument
    );
  };

  const buildConditionTask = (
    taskId: string,
    taskProperties: ConditionalTaskProperties
  ) => {
    const relatedEdges = labwareDependencyEdges.filter(
      (e) => e.source === taskId
    );

    const ifTaskId = relatedEdges.find(
      (e) => e.sourceHandle?.split('.')[2] === '0'
    )?.target;

    const elseTaskId = relatedEdges.find(
      (e) => e.sourceHandle?.split('.')[2] === '1'
    )?.target;

    const ifIsHoisted = !!ifTaskId && hoistedTaskIds.has(ifTaskId);
    const elseIsHoisted = !!elseTaskId && hoistedTaskIds.has(elseTaskId);

    if (ifTaskId && !ifIsHoisted) childTaskIds.add(ifTaskId);
    if (elseTaskId && !elseIsHoisted) childTaskIds.add(elseTaskId);

    const ifTask = ifTaskId && !ifIsHoisted ? buildTaskById(ifTaskId) : null;
    const elseTask =
      elseTaskId && !elseIsHoisted ? buildTaskById(elseTaskId) : null;

    const labwareIdsInTask = Object.values(
      labwareIdsForTaskByTaskId[taskId] ?? []
    ).flatMap((labwareId) => (labwareId === null ? [] : [labwareId]));

    const providerIds = new Set<string>();

    for (const labwareId of labwareIdsInTask) {
      const entry = labwareAndSourceTaskByTaskId[taskId]?.find(
        (lw) => lw.labwareId === labwareId
      );

      const sourceTaskId = entry?.sourceTaskId ?? null;
      const sourceTaskIds =
        entry?.sourceTaskIds && entry.sourceTaskIds.length > 1
          ? entry.sourceTaskIds
          : undefined;

      const initialIds: string[] = sourceTaskIds
        ? [...sourceTaskIds]
        : sourceTaskId !== null
        ? [sourceTaskId]
        : [];

      const resolvedForLabware = Array.from(
        new Set(
          initialIds.flatMap((id) => {
            const isContinuousLoading =
              taskPropertiesById[id]?.propertiesType ===
              PropertiesType.ContinuousLoading;

            if (!isContinuousLoading) return [id];

            const parentsEntry = labwareAndSourceTaskByTaskId[id]?.find(
              (lw) => lw.labwareId === labwareId
            );
            if (!parentsEntry) return [];

            if (
              parentsEntry.sourceTaskIds &&
              parentsEntry.sourceTaskIds.length > 0
            ) {
              return parentsEntry.sourceTaskIds;
            }
            if (parentsEntry.sourceTaskId) {
              return [parentsEntry.sourceTaskId];
            }
            return [];
          })
        )
      );

      for (const id of resolvedForLabware) providerIds.add(id);
    }

    const sourceTask: string | { task_ids: string[] } | null = (() => {
      const ids = Array.from(providerIds);

      if (ids.length > 1) return { task_ids: ids };
      if (ids.length === 1) return ids[0] || null;

      return null;
    })();

    return getConditionalTask({
      taskId,
      taskProperties,
      ifTask,
      elseTask,
      sourceTask,
    });
  };

  const buildContinuousLoadingTask = (taskId: string) => {
    const taskProperties = taskPropertiesById[taskId];
    if (
      !taskProperties ||
      taskProperties.propertiesType !== PropertiesType.ContinuousLoading
    )
      return null;

    const childTasks: Array<
      | ActionTaskInput
      | CodeTaskInput
      | DataConnectorInput
      | ConditionalInput
      | ContinuousLoadingInput
    > = [];

    const initialRelatedEdges = labwareDependencyEdges.filter(
      (e) => e.source === taskId
    );
    let currentTaskId = initialRelatedEdges[0]?.target;

    while (currentTaskId) {
      const currentIsHoisted = hoistedTaskIds.has(currentTaskId);

      if (currentIsHoisted) {
        break;
      }

      childTaskIds.add(currentTaskId);

      const task = buildTaskById(currentTaskId);
      if (task) {
        childTasks.push(task);
      }

      const nextEdges = labwareDependencyEdges.filter(
        (e) => e.source === currentTaskId
      );
      currentTaskId = nextEdges[0]?.target;
    }

    const labwareIdsInTask = labwareIdsForTaskByTaskId[taskId] ?? {};
    const labwareId =
      Object.values(labwareIdsInTask).find((id) => id !== null) ?? '';

    return getContinuousLoadingTask({
      taskId,
      labwareId,
      taskProperties,
      childTasks,
    });
  };

  for (const taskId of taskIds) {
    if (childTaskIds.has(taskId)) continue;

    const task = buildTaskById(taskId);
    if (task) validTasks[taskId] = task;
  }

  const isDataDependencyWithTaskId = (
    dep: unknown
  ): dep is { task_id: string } => {
    return (
      typeof dep === 'object' &&
      dep !== null &&
      'task_id' in dep &&
      typeof (dep as { task_id: unknown }).task_id === 'string'
    );
  };

  const buildMissingDependencyTasks = (dependencyIds: Set<string>): void => {
    for (const depTaskId of dependencyIds) {
      if (childTaskIds.has(depTaskId)) continue;
      if (validTasks[depTaskId]) continue;

      const depTask = buildTaskById(depTaskId);
      if (depTask) {
        validTasks[depTaskId] = depTask;
      }
    }
  };

  const referencedDependencyTaskIds = new Set<string>();

  for (const deps of Object.values(taskDependenciesByTaskId)) {
    for (const depTaskId of Object.values(deps ?? {})) {
      if (depTaskId) {
        referencedDependencyTaskIds.add(depTaskId);
      }
    }
  }

  for (const deps of Object.values(dataDependenciesByTaskId)) {
    for (const dep of Object.values(deps ?? {})) {
      if (isDataDependencyWithTaskId(dep) && dep.task_id) {
        referencedDependencyTaskIds.add(dep.task_id);
      }
    }
  }

  buildMissingDependencyTasks(referencedDependencyTaskIds);

  const dependencyIdsFromBuiltTasks = new Set<string>();

  for (const task of Object.values(validTasks)) {
    const depsArray = (task as any)?.dependencies;
    if (Array.isArray(depsArray)) {
      for (const id of depsArray) {
        if (id) dependencyIdsFromBuiltTasks.add(id);
      }
    }

    if (
      depsArray &&
      typeof depsArray === 'object' &&
      !Array.isArray(depsArray)
    ) {
      for (const value of Object.values(depsArray)) {
        const tid = (value as any)?.task_id;
        if (typeof tid === 'string' && tid)
          dependencyIdsFromBuiltTasks.add(tid);
      }
    }
  }

  buildMissingDependencyTasks(dependencyIdsFromBuiltTasks);

  if (errors.length > 0) {
    return err(errors);
  }

  return ok(validTasks);
};

export const walkTaskListForValidationForSave = (
  taskIds: string[],
  graph: Graph,
  labwareIdsForTaskByTaskId: LabwareByHandleIndexByTaskId,
  taskPropertiesById: Record<string, AllTaskProperties>,
  timeEstimateValuesByTaskId: TimeEstimateValuesByTaskId,
  taskDependenciesByTaskId: TaskDependenciesByTaskId,
  dataDependenciesByTaskId: DataDependenciesByTaskId,
  instrumentsById: Record<string, InstrumentForLocation>,
  driverActionConfigs: DriverActionConfigsByActionNameByVersionedDriverName,
  labwareAndSourceNodeByTaskId: LabwareAndSourceTaskByTaskId,
  labwareDependencyEdges: GraphEdges
): WorkflowInput['tasks'] => {
  const result = walkTaskListForValidation(
    taskIds,
    graph,
    labwareIdsForTaskByTaskId,
    taskPropertiesById,
    timeEstimateValuesByTaskId,
    taskDependenciesByTaskId,
    dataDependenciesByTaskId,
    instrumentsById,
    driverActionConfigs,
    labwareAndSourceNodeByTaskId,
    labwareDependencyEdges
  );

  if (result.isError) {
    // NOTE(IL, 2025-01-16): since we've been using the layout key to save the tasks VB cares about,
    // and since we don't really want to execute/orchestrate workflows that have VB errors,
    // this is actually okay. So it's a logger.log and not a logger.warning or logger.error.
    logger.log(
      `walkTaskListForValidationForSave got serialization errors: ${JSON.stringify(
        result.errors,
        null,
        2
      )}`
    );
    // return no tasks for save if there are errors
    return {};
  }
  return result.data;
};
